/**
 * Beat 7's authored exit from the loading yard to the outside palette.
 *
 * The bloom is one render-step director for the three independent zone readers:
 * toon lighting and fog, the post grade's chroma ceiling, and sky/exposure.
 * Keeping them here prevents a plausible-looking transition from warming and
 * clearing without ever becoming saturated.
 */

import * as THREE from 'three';
import { fogDensity } from '@/render/lighting';
import type { ZonePalette } from '@/render/palette';
import type { PostStack } from '@/render/post/stack';
import { applyZone, getExposureReference, setExposureReference } from '@/render/toon';

/**
 * The representative surface list, interpolated element-wise.
 *
 * Split out rather than inlined because it has an endpoint requirement the rest
 * of the palette shares and which is easy to lose in a `map`: at t = 0 the result
 * must be `from`'s values EXACTLY, with no float drift, because `surfaces[3]` is
 * uploaded as a colour and a rust tint one bit off its authored value is a
 * difference nobody can see and every screenshot comparison can.
 */
function lerpSurfaces(
  from: readonly number[],
  to: readonly number[],
  t: number,
): readonly number[] {
  if (t === 0) return from;
  if (t === 1) return to;
  const shared = Math.min(from.length, to.length);
  const out: number[] = [];
  const colour = new THREE.Color();
  for (let i = 0; i < shared; i += 1) {
    // `noUncheckedIndexedAccess`: both indices are below both lengths, so the
    // fallbacks are unreachable rather than lenient.
    colour.setHex(from[i] ?? 0x000000);
    out.push(colour.lerp(new THREE.Color(to[i] ?? 0x000000), t).getHex());
  }
  // A zone with more entries than its partner keeps the destination's tail. The
  // alternative — truncating — would hand the mood board a short list.
  for (let i = shared; i < to.length; i += 1) out.push(to[i] ?? 0x000000);
  return out;
}

/** A `ZonePalette` interpolated between two authored ones. */
export function lerpZonePalette(from: ZonePalette, to: ZonePalette, t: number): ZonePalette {
  const progress = THREE.MathUtils.clamp(t, 0, 1);
  const colour = (fromHex: number, toHex: number): number => {
    if (progress === 0) return fromHex;
    if (progress === 1) return toHex;
    return new THREE.Color(fromHex).lerp(new THREE.Color(toHex), progress).getHex();
  };

  return {
    id: `${from.id}→${to.id}`,
    label: `${from.label} → ${to.label}`,
    brief: `Interpolated from ${from.label} to ${to.label}.`,
    key: colour(from.key, to.key),
    fill: colour(from.fill, to.fill),
    shadow: colour(from.shadow, to.shadow),
    fog: colour(from.fog, to.fog),
    // ── Interpolated, and the first build of this function did not ────────────
    //
    // It took `to.surfaces` unchanged, on the argument that `surfaces` is the
    // mood board's per-material authoring data and has no runtime reader.
    //
    // **`applyZone()` reads it.** `surfaces[3]` is the rust colour
    // (`src/render/toon.ts`, and the comment there says so). Taking the
    // destination's array made every RUSTABLE surface — `metal`, and nothing
    // else — take the `outside` zone's `0xa9d4e8` pale sky blue from t = 0
    // onward, instead of the yard's `0x8b93a1` grey. In beat 7 that is nineteen
    // bays of galvanized perimeter fence, wrongly coloured in the pre-bloom
    // frame, which is the shot where she is trying to break through them.
    //
    // Found by reading `applyZone`'s body rather than its summary, which is the
    // lesson: a value with "no runtime reader" is a claim about every caller.
    //
    // Element-wise over the shared prefix, with any surplus taken from `to`. The
    // two zones this actually runs between both declare five, so the surplus
    // branch is defensive — but a short array here would silently break the mood
    // board, which is the one thing that genuinely does read the whole list.
    surfaces: lerpSurfaces(from.surfaces, to.surfaces, progress),
    chromaCeiling: THREE.MathUtils.lerp(from.chromaCeiling, to.chromaCeiling, progress),
    haze: THREE.MathUtils.lerp(from.haze, to.haze, progress),
    ...(to.accent === undefined ? {} : { accent: to.accent }),
    ...(to.accentCeiling === undefined ? {} : { accentCeiling: to.accentCeiling }),
    ...(to.grime === undefined ? {} : { grime: to.grime }),
    ...(to.straw === undefined ? {} : { straw: to.straw }),
  };
}

export interface BloomOptions {
  /** The post stack, or null when it is disabled (`?post=0`). */
  readonly post: PostStack | null;
  /** The scene, for `backgroundIntensity`. */
  readonly scene: THREE.Scene;
  /** The room's fog scale, so the interpolated haze lands where the room's does. */
  readonly fogScale?: number;
  /** Exposure reference at t=0 and t=1. */
  readonly exposureFrom: number;
  readonly exposureTo: number;
  /** `scene.backgroundIntensity` at t=0 and t=1. */
  readonly skyFrom: number;
  readonly skyTo: number;
}

export interface BloomCensus {
  /** Raw t, and the three eased values, so a test can see WHICH curve moved. */
  t: number;
  lightEase: number;
  hazeEase: number;
  chromaEase: number;
  /** What was actually written. */
  chromaCeiling: number;
  fogDensity: number;
  keyHex: number;
  fogHex: number;
  skyIntensity: number;
  exposureReference: number;
}

/**
 * One authored zone interpolation feeding every renderer state the bloom needs.
 *
 * Neither endpoint declares grime, so `applyZone()` keeps both frames unsoiled;
 * a future bloom from a soiled zone would need `setGrime()` as its explicit seam.
 */
export class Bloom {
  private readonly fogScale: number;
  private readonly initialCensus: BloomCensus;
  private lastCensus: BloomCensus;
  private lastProgress = 0;
  private hasApplied = false;

  public constructor(
    private readonly from: ZonePalette,
    private readonly to: ZonePalette,
    private readonly options: BloomOptions,
  ) {
    this.fogScale = options.fogScale ?? 1;
    this.initialCensus = this.createCensus(0);
    this.lastCensus = this.initialCensus;
  }

  /**
   * Drive all three readers. `t` is raw 0..1 progress; the easing is inside.
   * Called on the RENDER step, at display rate. Never from `fixedUpdate`.
   */
  public apply(t: number): void {
    const progress = THREE.MathUtils.clamp(t, 0, 1);
    if (this.hasApplied && progress < this.lastProgress) return;

    const census = this.createCensus(progress);
    const lightHazePalette = lerpZonePalette(this.from, this.to, census.lightEase);

    // Keep the light and haze palettes visibly separate even though both curves
    // currently share smoothstep; combining them would hide the intended readers.
    lightHazePalette.fog = lerpZonePalette(this.from, this.to, census.hazeEase).fog;
    lightHazePalette.haze = THREE.MathUtils.lerp(this.from.haze, this.to.haze, census.hazeEase);
    census.fogDensity = fogDensity(lightHazePalette.haze, this.fogScale);
    applyZone(lightHazePalette, census.fogDensity);

    // `setZone()` clears this override. Beat 7 never calls it mid-bloom; a later
    // caller would silently reset the ceiling to an authored endpoint value.
    this.options.post?.setChromaCeiling(census.chromaCeiling);
    this.options.scene.backgroundIntensity = census.skyIntensity;
    setExposureReference(census.exposureReference);

    // ── The census is READ BACK from the sinks, not from the arithmetic ───────
    //
    // Everything above this line was computed; everything on it is what the
    // renderer actually holds. The first build reported the computed values, and
    // the consequence is that **deleting the `setChromaCeiling` call would have
    // left the bloom gate green** — a gate asserting a number this class had worked
    // out for itself, about a uniform it had not written. That is the worst
    // available failure for this shot wearing a passing test
    // (`docs/beat-7-plan.md`, the gate section).
    //
    // Three of the four sinks can be read back. `applyZone`'s colours cannot
    // without a getter per uniform, so `keyHex`/`fogHex` stay computed — say so
    // rather than let the next reader assume the whole census is a measurement.
    census.chromaCeiling = this.options.post === null
      ? census.chromaCeiling
      : this.options.post.chromaCeiling;
    census.skyIntensity = this.options.scene.backgroundIntensity;
    census.exposureReference = getExposureReference();

    this.lastProgress = progress;
    this.lastCensus = census;
    this.hasApplied = true;
  }

  /** The raw `t` last applied, clamped and monotonic. */
  public get progress(): number {
    return this.lastProgress;
  }

  /** Everything it drove, last call. The gate reads this. */
  public census(): BloomCensus {
    return { ...this.lastCensus };
  }

  private createCensus(t: number): BloomCensus {
    const lightEase = THREE.MathUtils.smoothstep(t, 0, 1);
    const hazeEase = THREE.MathUtils.smoothstep(t, 0, 1);
    // Chroma stays at 0.22 through t = 0.35 (2.45 s of a seven-second bloom),
    // then traverses its whole range in the remaining 4.55 s.
    const chromaEase = THREE.MathUtils.smoothstep(
      THREE.MathUtils.clamp((t - 0.35) / 0.65, 0, 1),
      0,
      1,
    );
    const lightPalette = lerpZonePalette(this.from, this.to, lightEase);
    const hazePalette = lerpZonePalette(this.from, this.to, hazeEase);
    const chromaPalette = lerpZonePalette(this.from, this.to, chromaEase);

    return {
      t,
      lightEase,
      hazeEase,
      chromaEase,
      chromaCeiling: chromaPalette.chromaCeiling,
      fogDensity: fogDensity(hazePalette.haze, this.fogScale),
      keyHex: lightPalette.key,
      fogHex: hazePalette.fog,
      skyIntensity: THREE.MathUtils.lerp(this.options.skyFrom, this.options.skyTo, lightEase),
      exposureReference: THREE.MathUtils.lerp(
        this.options.exposureFrom,
        this.options.exposureTo,
        lightEase,
      ),
    };
  }
}
